import { MessageBar, MessageBarBody, MessageBarTitle } from '@fluentui/react-components';

import { detectEnvironmentDrift, useEnvironment } from '../context/EnvironmentContext';
import { IDENTIFIER } from '../theme/tokens';

// FD-422 / FD-ADR-040 §8 — "the console and the deployment disagree about which
// environment this is."
//
// THIS IS THE HIGHEST-VALUE OF THE FOUR DEFENCES AND THE ONLY ONE THAT SURFACES A
// CONDITION NOTHING ELSE CAN SEE. A fleet inventory whose entry says `Production`
// beside a `managerBaseUrl` that is staging's produces a console where every
// request succeeds, every page renders, every number is real — and the word the
// operator is reading while they decide whether to abort a wave is wrong. The
// selector, the tone badge and the echo all render that same wrong label
// faithfully, because they all read the same config. Only the server's own
// `ENVIRONMENT_NAME`, fetched from the Manager that is actually answering, can
// contradict it.
//
// NOT DISMISSIBLE, unlike `EnvironmentNotice` — and the difference is what each
// reports. That notice records how you ARRIVED (a link named an environment this
// console does not serve), a fact that stops mattering once you have read it.
// This reports a LIVE condition that affects every action on every page for as
// long as it holds. A dismiss button would let it be cleared in exactly the
// session where it matters, and nothing would bring it back.
//
// It needs the boot probe to have answered, so it renders nothing until then —
// fail-open, consistent with the probe. An environment whose Manager reports no
// `ENVIRONMENT_NAME` at all is silence, not disagreement (`detectEnvironmentDrift`).

export default function EnvironmentDriftBanner() {
  const { environment, activeHealth } = useEnvironment();

  const serverName = activeHealth.environmentName;
  if (!detectEnvironmentDrift(environment, serverName)) return null;

  return (
    <div className="mb-6">
      <MessageBar intent="warning">
        <MessageBarBody>
          <MessageBarTitle>
            This console and the deployment it is calling disagree
          </MessageBarTitle>{' '}
          {/* BOTH VALUES ARE NAMED, and the Manager origin with them. "Something
              is wrong" is not actionable; "this console calls it Production, the
              Manager at <host> reports prod" tells an operator which of the two
              to go and fix. */}
          {/* FD-586 — "this console calls it" is only true of a label the
              console's own config supplied. A label that came from the server
              (or is just the id again) is not the console's claim, and quoting
              it here would present the Manager's own word as the thing the
              Manager contradicts — so only the id is named then, which is also
              the only console-side value the drift check compared. */}
          This console calls it{' '}
          {environment.labelSource === 'config' ? (
            <>
              <strong>{environment.label}</strong> (
              <span style={{ ...IDENTIFIER }}>{environment.id}</span>)
            </>
          ) : (
            <span style={{ ...IDENTIFIER }}>{environment.id}</span>
          )}
          , but the Manager at{' '}
          <span style={{ ...IDENTIFIER }}>{environment.managerBaseUrl}</span> reports
          its environment as <span style={{ ...IDENTIFIER }}>{serverName}</span>. One
          of the two is wrong — check the fleet inventory and the deployment&rsquo;s
          <span style={{ ...IDENTIFIER }}> ENVIRONMENT_NAME</span> before taking any
          action here.
        </MessageBarBody>
      </MessageBar>
    </div>
  );
}
